/**
 * THE browser's one way to offer and to fire a user lifecycle transition.
 *
 * Foundations 2 (Users rail), B2c-T3. principles.md #1 ("one store, one
 * resolver, one owner per noun") and #2 ("done means the old path is deleted").
 *
 * ─── What this owns ───────────────────────────────────────────────────────
 *
 * Nothing about the RULES. The rules are the transition table
 * (`packages/shared/src/schemas/user-lifecycle.ts`), projected through `can()`
 * by `GET /users/:id/lifecycle`, and every screen renders that projection:
 * which transitions exist from this account's state, which THIS caller may
 * perform, what each is called, its glyph, its emphasis, whether it prompts,
 * whether it needs a reason, what to warn about, and — for the ones the
 * caller may not perform — why.
 *
 * What this file owns is the MECHANICS that would otherwise be written twice:
 *
 *   - the icon KEY → component binding (presentation, not knowledge);
 *   - which endpoint each surface's destination goes to (`status` → PATCH
 *     /users/:id/status, `delete` → DELETE /users/:id, `invitation` → the
 *     host's own runner);
 *   - the confirmation a transition asks for: a reason prompt for the status
 *     surface, and the canonical typed-confirm gate for the removal.
 *
 * ─── Why it is a module and not two copies ────────────────────────────────
 *
 * B2a-T2 moved the transition table out of the browser and the detail page
 * started rendering the server's answer. The ROSTER did not: its row menu
 * offered "View details / Copy email / Manage on detail page…" and every
 * lifecycle action lived one navigation away, because the row had nowhere to
 * put the confirmation, the reason capture and the refusal sentence. B2c-T3
 * puts the same controls on the row — and the way to do that without growing
 * a second copy of the firing logic is for both surfaces to call this.
 *
 * The host decides what a control LOOKS like (the detail page draws buttons in
 * its action bar, the roster draws items in its row menu). This decides what
 * pressing one DOES.
 */

import { useState, type ReactNode } from 'react';
import {
  CheckCircle2, Mail, XCircle, Lock, Unlock, PauseCircle, PlayCircle,
  UserX, RotateCcw, Trash2, Info,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { getApiErrorCode, getApiErrorMessage } from '@/lib/api-error';
import { useUpdateUserStatus, useDeleteUser } from '@/lib/use-users';
import type {
  LifecycleStatusDestination,
  LifecycleTransitionIcon,
  UserLifecycleAllowedTransition,
} from '@argusai/shared/schemas/user-lifecycle';

// ─── Lifecycle iconography ───────────────────────────────────────────────────
//
// The ONLY lifecycle knowledge left in the browser, and it is not knowledge:
// it binds the icon KEY the server sent to a component. No rule, no gate, no
// label, no ordering. A key this build does not recognise still renders its
// control — with a neutral glyph — because a missing icon must never be the
// reason an operator cannot see an action they are allowed to take.
export const LIFECYCLE_ICONS: Record<LifecycleTransitionIcon, typeof Lock> = {
  'check': CheckCircle2,
  'mail': Mail,
  'mail-x': XCircle,
  'lock': Lock,
  'unlock': Unlock,
  'pause': PauseCircle,
  'play': PlayCircle,
  'user-x': UserX,
  'rotate': RotateCcw,
  'trash': Trash2,
};

export function LifecycleIcon({
  icon,
  className,
}: {
  icon: LifecycleTransitionIcon;
  className?: string;
}) {
  const Glyph = LIFECYCLE_ICONS[icon] ?? Info;
  return <Glyph className={cn('h-3.5 w-3.5 mr-1.5', className)} />;
}

/** The person a transition is being walked for. */
export interface LifecycleTarget {
  id: string;
  fullName: string;
  email: string;
}

export interface UseLifecycleTransitionOptions {
  /**
   * Which endpoint an INVITATION-surface destination goes to, for a host that
   * can walk one. The invitation edges are the invitation flow's, not the
   * status endpoint's, and each host reaches a different subset of them: the
   * detail page can re-invite, the Invitations tab can revoke. A destination
   * with no entry here is not offered, because a control that cannot do
   * anything is the dead button this rail keeps deleting.
   */
  invitationActions?: Record<string, { run: () => void; pending: boolean }>;
  /** Fired after the person has been removed from this tenant. */
  onRemoved?: () => void;
}

export interface LifecycleTransitionRunner {
  /**
   * Offer-side entry point: the host calls this with the entry the SERVER
   * returned. Whether it prompts, what the prompt says and which endpoint it
   * calls are all decided from that entry.
   */
  start: (transition: UserLifecycleAllowedTransition) => void;
  /**
   * The invitation-surface endpoint this host registered for `to`, or
   * `undefined` when it cannot walk that edge.
   *
   * Review B2cT3-R3 — ONE dispatch, asked once. The detail page used to keep
   * its own `INVITATION_SURFACE_ACTIONS` map and both filter and fire off it
   * directly, beside an `invitationActions` option here that no caller passed:
   * two mechanisms for one job, in the module whose stated purpose is that
   * there is only one. The host still owns WHICH endpoint (that is a property
   * of the screen); asking whether the control can be drawn, and firing it,
   * both go through the runner now.
   */
  invitationAction: (
    to: string
  ) => { run: () => void; pending: boolean } | undefined;
  /** A write is in flight — hosts disable their controls on it. */
  pending: boolean;
  /** The confirmation surfaces. Mount once inside the host. */
  dialogs: ReactNode;
}

/**
 * Fire lifecycle transitions for one person, with the confirmation each one
 * declares.
 *
 * The status surface's prompt is the AlertDialog with the reason capture the
 * detail page has always used. The DELETE surface's is the canonical
 * `TypedConfirmDialog` — the rail's existing destructive gate — because
 * removing somebody cascades their role assignments, group memberships and
 * department memberships, and a misplaced click on a row menu must not be able
 * to do that. The consequences it lists are the SERVER's `warning` sentence,
 * not a list this file keeps in step by hand.
 */
export function useLifecycleTransition(
  target: LifecycleTarget,
  options: UseLifecycleTransitionOptions = {}
): LifecycleTransitionRunner {
  const updateStatus = useUpdateUserStatus();
  const removeUser = useDeleteUser();

  // The dialog's content is the SERVER'S transition entry, held as-is. It used
  // to be a hand-built `{ title, description, actionLabel, requireReason,
  // destructive }` composed at each call site, which is where the browser's
  // copy of the rules actually lived.
  const [confirm, setConfirm] =
    useState<UserLifecycleAllowedTransition | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [removal, setRemoval] =
    useState<UserLifecycleAllowedTransition | null>(null);
  const [removalError, setRemovalError] = useState<string | null>(null);

  const fireTransition = async (
    to: LifecycleStatusDestination,
    reason?: string
  ) => {
    try {
      await updateStatus.mutateAsync({ userId: target.id, to, reason });
      toast.success(`Status changed to ${to}`);
      setConfirm(null);
      setReasonText('');
    } catch (err) {
      const code = getApiErrorCode(err);
      // A refused transition answers 409 INVALID_TRANSITION carrying the
      // table's own sentence; `getApiErrorMessage` surfaces it verbatim, so
      // the operator reads the rule rather than a code.
      const msg = getApiErrorMessage(err, 'Status change failed');
      toast.error(`${code}: ${msg}`);
    }
  };

  const fireRemoval = async () => {
    setRemovalError(null);
    try {
      await removeUser.mutateAsync({ userId: target.id });
      toast.success(`${target.fullName} was removed from this organization`);
      setRemoval(null);
      options.onRemoved?.();
    } catch (err) {
      // The dialog stays open (`closeOnConfirm={false}`) and shows the
      // server's own sentence: a refused removal answers 409
      // INVALID_TRANSITION with the table's reason, and a 403 carries the
      // permission it wanted. A destructive action whose failure lands in a
      // toast behind a closed dialog is a destructive action nobody can see
      // fail.
      setRemovalError(getApiErrorMessage(err, 'Could not remove this user'));
    }
  };

  /**
   * Fire a transition, prompting first when the server says to.
   *
   * WHICH transitions prompt, WHICH need a reason and WHAT the prompt says are
   * all fields on the entry the server sent. Nothing here decides any of it —
   * only which door the destination goes through, which is a property of the
   * SURFACE the entry names.
   */
  const start = (transition: UserLifecycleAllowedTransition) => {
    if (transition.surface === 'delete') {
      setRemovalError(null);
      setRemoval(transition);
      return;
    }
    if (transition.surface === 'invitation') {
      const action = options.invitationActions?.[transition.to];
      if (!action) {
        // REFUSE IN THE OPEN (review B2cT3-R3). This used to `?.run()` and
        // return, so a host that offered an invitation-surface entry without
        // registering its endpoint got a control that did nothing at all: no
        // request, no toast, no error — the dead button this file exists to
        // delete. Hosts ask `invitationAction()` before they draw the control,
        // so reaching this line is a wiring bug, and it says so rather than
        // swallowing the click.
        toast.error(
          `“${transition.label}” cannot be done from this screen — open the Invitations tab.`
        );
        return;
      }
      action.run();
      return;
    }
    if (transition.requiresConfirm || transition.requiresReason) {
      setConfirm(transition);
      setReasonText('');
      return;
    }
    void fireTransition(transition.to as LifecycleStatusDestination);
  };

  const dialogs = (
    <>
      {/* ── Status-surface confirmation ───────────────────────────────── */}
      <AlertDialog
        open={confirm !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirm(null);
            setReasonText('');
          }
        }}
      >
        <AlertDialogContent>
          {/* Title, prose and emphasis are the SERVER'S transition entry.  */}
          {/* They used to be five literal objects built at five call sites, */}
          {/* which is where the browser's copy of the rules actually lived. */}
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm ? `${confirm.label} ${target.fullName}?` : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>{confirm?.warning}</AlertDialogDescription>
          </AlertDialogHeader>
          {confirm?.requiresReason && (
            <div className="space-y-1.5">
              <Label htmlFor="lifecycle-reason" className="text-xs">
                Reason <span className="text-red-600">*</span>
              </Label>
              <Textarea
                id="lifecycle-reason"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="e.g., compliance review, departure, role change…"
                className="text-xs"
                rows={3}
              />
              <p className="text-[10px] text-muted-foreground">
                Minimum 5 characters. The reason is recorded in the audit log and visible to compliance reviewers.
              </p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateStatus.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={
                updateStatus.isPending ||
                (confirm?.requiresReason && reasonText.trim().length < 5)
              }
              className={cn(
                // `confirm !== null &&` matters: `undefined !== 'normal'` is
                // true, so the bare comparison read "not normal OR not set"
                // and sent every unrecognised severity to the red treatment by
                // default (review B2aT2-BF-07). Emphasis is decided by the
                // severity the server sent, or not at all.
                confirm !== null &&
                  confirm.severity !== 'normal' &&
                  'bg-red-600 hover:bg-red-700 focus-visible:ring-red-600'
              )}
              onClick={() => {
                if (confirm) {
                  void fireTransition(
                    confirm.to as LifecycleStatusDestination,
                    confirm.requiresReason ? reasonText.trim() : undefined
                  );
                }
              }}
            >
              {updateStatus.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {confirm?.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete-surface confirmation — the rail's typed gate ────────── */}
      {/* B2c-T3. Removing a person cascades: the consequences listed are    */}
      {/* the table's own `warning` for the edge, which is where that list   */}
      {/* is written once (`DELETE_WARNING` in the shared table). This       */}
      {/* dialog states no rule of its own — it names WHO, and quotes the    */}
      {/* server for WHAT.                                                   */}
      <TypedConfirmDialog
        open={removal !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRemoval(null);
            setRemovalError(null);
          }
        }}
        title={removal ? `${removal.label} ${target.fullName}` : ''}
        description={`${target.fullName} (${target.email}) will be removed from this organization.`}
        consequences={removal?.warning ? [removal.warning] : []}
        confirmPhrase={target.email}
        confirmLabel={removal?.label ?? 'Delete'}
        closeOnConfirm={false}
        errorMessage={removalError}
        pending={removeUser.isPending}
        onConfirm={() => void fireRemoval()}
      />
    </>
  );

  return {
    start,
    invitationAction: (to: string) => options.invitationActions?.[to],
    pending: updateStatus.isPending || removeUser.isPending,
    dialogs,
  };
}
